import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CashDiscrepancy, CashDiscrepancyCreateRequest, CashDiscrepancyReport, CashDiscrepancyStats } from '../Module/CashDiscrepancy.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CashDiscrepancyService {
  private get apiUrl(): string {
    return `${environment.apiUrl}/cash-discrepancies`;
  }

  constructor(private http: HttpClient) { }

  // Créer un nouvel écart de caisse
  createCashDiscrepancy(request: CashDiscrepancyCreateRequest): Observable<CashDiscrepancy> {
    const params = new HttpParams()
      .set('date', request.date)
      .set('realAmount', request.realAmount.toString())
      .set('systemAmount', request.systemAmount.toString())
      .set('comment', request.comment || '');

    return this.http.post<CashDiscrepancy>(this.apiUrl, null, { params });
  }

  // Obtenir tous les écarts de caisse
  getAllCashDiscrepancies(): Observable<CashDiscrepancy[]> {
    return this.http.get<CashDiscrepancy[]>(this.apiUrl);
  }

  // Obtenir les écarts de caisse avec pagination (trié par date descendante)
  getPaginatedCashDiscrepancies(page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<any>(`${this.apiUrl}/paginated`, { params });
  }

  // Obtenir un écart par ID
  getCashDiscrepancyById(id: number): Observable<CashDiscrepancy> {
    return this.http.get<CashDiscrepancy>(`${this.apiUrl}/${id}`);
  }

  // Obtenir les écarts en attente d'approbation
  getPendingCashDiscrepancies(): Observable<CashDiscrepancy[]> {
    return this.http.get<CashDiscrepancy[]>(`${this.apiUrl}/pending`);
  }

  // Approuver un écart
  approveCashDiscrepancy(id: number): Observable<CashDiscrepancy> {
    return this.http.post<CashDiscrepancy>(`${this.apiUrl}/${id}/approve`, {});
  }

  // Rejeter un écart
  rejectCashDiscrepancy(id: number): Observable<CashDiscrepancy> {
    return this.http.post<CashDiscrepancy>(`${this.apiUrl}/${id}/reject`, {});
  }

  // Mettre à jour un écart
  updateCashDiscrepancy(id: number, realAmount?: number, systemAmount?: number, comment?: string): Observable<CashDiscrepancy> {
    const params = new HttpParams()
      .set('realAmount', realAmount ? realAmount.toString() : '')
      .set('systemAmount', systemAmount ? systemAmount.toString() : '')
      .set('comment', comment || '');

    return this.http.put<CashDiscrepancy>(`${this.apiUrl}/${id}`, null, { params });
  }

  // Supprimer un écart
  deleteCashDiscrepancy(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  // Générer un rapport
  generateReport(startDate: string, endDate: string): Observable<CashDiscrepancy[]> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get<CashDiscrepancy[]>(`${this.apiUrl}/report`, { params });
  }

  // Calculer le total des écarts approuvés
  calculateTotalApproved(startDate: string, endDate: string): Observable<number> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get<number>(`${this.apiUrl}/total-approved`, { params });
  }

  // Vérifier s'il y a déjà un écart pour une date
  checkDateExists(date: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/check-date?date=${date}`);
  }
  


  // Calculer les statistiques
  calculateStats(discrepancies: CashDiscrepancy[]): CashDiscrepancyStats {
    const stats: CashDiscrepancyStats = {
      totalDiscrepancies: discrepancies.length,
      pendingDiscrepancies: 0,
      approvedDiscrepancies: 0,
      rejectedDiscrepancies: 0,
      totalAmount: 0,
      positiveAmount: 0,
      negativeAmount: 0
    };

    discrepancies.forEach(discrepancy => {
      switch (discrepancy.status) {
        case 'En attente':
          stats.pendingDiscrepancies++;
          break;
        case 'Approuvé':
          stats.approvedDiscrepancies++;
          break;
        case 'Rejeté':
          stats.rejectedDiscrepancies++;
          break;
      }

      stats.totalAmount += discrepancy.discrepancyAmount;
      if (discrepancy.discrepancyAmount > 0) {
        stats.positiveAmount += discrepancy.discrepancyAmount;
      } else {
        stats.negativeAmount += Math.abs(discrepancy.discrepancyAmount);
      }
    });

    return stats;
  }

  // Formater la date pour l'affichage
  formatDate(dateString: string): string {
    if (!dateString) {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', dateString, error);
      return 'Date invalide';
    }
  }

  // Formater le montant pour l'affichage
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD'
    }).format(amount);
  }

  // Obtenir la classe CSS pour le statut
  getStatusClass(status: string): string {
    switch (status) {
      case 'En attente':
        return 'text-yellow-600 bg-yellow-100';
      case 'Approuvé':
        return 'text-green-600 bg-green-100';
      case 'Rejeté':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }

  // Obtenir la classe CSS pour le montant d'écart
  getDiscrepancyClass(amount: number): string {
    if (amount > 0) {
      return 'text-green-600';
    } else if (amount < 0) {
      return 'text-red-600';
    } else {
      return 'text-gray-600';
    }
  }
} 